/**
 * Just added, inside the Add card (D222, D224, `justAdded.ts`): every line typed into the box,
 * until the person has seen it - the human-validation step for the AI's work.
 *
 * A line is a status pill and then the thing itself, as heavy as it is in its own list so
 * whatever the AI got wrong can be put right on the spot: a task is a real task row (energy,
 * due, star, snooze, edit in place), a note a real note row. A line the AI could not place is
 * the Inbox's own row, with its forms and its bulb. "You wrote:" shows the person's words when
 * the AI's version differs.
 *
 * The ✓ lets a line go. A filed line counts down on its ✓ (the Inbox bulb's ring) when the
 * Display setting asks for it, and anything that touches the row stops the count for good: the
 * pointer waits it, a tap, focus or a menu opening ends it. Lines the AI could not place never
 * count down; they go to the Inbox when sent there.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type Area, type InboxItem, type Note, type Project, type Task } from "../api";
import { refineNoteInPlace } from "../aiRefine";
import { useAiTierAvailability } from "../aiTiers";
import type { AddEntry, JustAddedActions } from "../justAdded";
import { openFiled, useEditPopups } from "../editPopupContext";
import { ITEM_EDITED_EVENT } from "../editPopupEvents";
import { autoClearDelay, prefersReducedMotion } from "../inboxAiRetryMotion";
import { pausableTimeout, type PausableTimeout } from "../pausableTimeout";
import { postNotice } from "../notices";
import { loadSettings } from "../settings";
import { basicTaskActions } from "../taskActions";
import { detailOf, needsLook, pillOf, type Kind } from "../addResultPill";
import { AddResultPill } from "./AddResultPill";
import { useFireworks } from "./fireworksContext";
import { Ring } from "./InboxAiRetryButton";
import { InboxRow } from "./InboxSection";
import { Check, Pencil } from "./icons";
import { NoteRow } from "./NotesSection";
import { todayLocalDate } from "./recurrence";
import { ProjectOutlineButton } from "./ProjectOutlineButton";
import { TaskDrawer } from "./TaskDrawer";
import { TaskRow, type TaskActions } from "./TaskList";

export function AddResults({
  entries,
  areas,
  projects,
  actions,
  onAcknowledge,
  onChanged,
}: {
  entries: AddEntry[];
  areas: Area[];
  projects: Project[];
  /** The Inbox row's calls, on an entry (`useJustAdded`). */
  actions: JustAddedActions;
  /** Let entries go (all of them, if no ids). */
  onAcknowledge: (ids?: string[]) => void;
  /** Something on a row changed what the lists around the card show. */
  onChanged?: () => void | Promise<void>;
}) {
  const announcement = useStateAnnouncement(entries);
  if (entries.length === 0) return <div className="sr-only" role="status" aria-live="polite" />;
  const toInbox = entries.filter(needsLook).length;
  const settled = entries.some((e) => !isWaiting(e));
  return (
    <div className="add-results" role="region" aria-label="Just added">
      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>
      <div className="add-results-head">
        <span className="small muted">Just added · {entries.length}</span>
        {toInbox > 0 && (
          <span className="small muted">
            {toInbox === 1 ? "1 will go" : `${toInbox} will go`} to your Inbox
          </span>
        )}
        {settled && (
          <button type="button" className="btn ghost small" onClick={() => onAcknowledge()}>
            Clear list
          </button>
        )}
      </div>
      <ul>
        {entries.map((entry) => (
          <AddResultRow
            key={entry.id}
            entry={entry}
            areas={areas}
            projects={projects}
            actions={actions}
            onAcknowledge={onAcknowledge}
            onChanged={onChanged}
          />
        ))}
      </ul>
    </div>
  );
}

/**
 * What a screen reader hears when a line stops waiting: "Created Project: Shed", "Not sure: buy
 * milk". The pill changing colour says it to everyone else. Only a change from waiting is told, so
 * the list loading on a page visit says nothing.
 */
function useStateAnnouncement(entries: AddEntry[]): string {
  const seen = useRef(new Map<string, AddEntry["state"]>());
  const [message, setMessage] = useState("");
  useEffect(() => {
    const told: string[] = [];
    for (const entry of entries) {
      const before = seen.current.get(entry.id);
      if (before && before !== entry.state && isWaitingState(before) && !isWaiting(entry)) {
        told.push(`${pillOf(entry).label}: ${entry.created?.title ?? entry.text}`);
      }
      seen.current.set(entry.id, entry.state);
    }
    if (told.length) setMessage(told.join(". "));
  }, [entries]);
  return message;
}

function isWaitingState(state: AddEntry["state"]): boolean {
  return state === "sending" || state === "queued" || state === "processing";
}

/** The AI (or the outbox) is still on it, so there is nothing to acknowledge yet. */
function isWaiting(entry: AddEntry): boolean {
  return isWaitingState(entry.state);
}

/** The person's words, when what was made reads differently. */
function wasReworded(entry: AddEntry): boolean {
  const made = entry.created?.title;
  return !!made && made.trim().toLowerCase() !== entry.text.trim().toLowerCase();
}

function AddResultRow({
  entry,
  areas,
  projects,
  actions,
  onAcknowledge,
  onChanged,
}: {
  entry: AddEntry;
  areas: Area[];
  projects: Project[];
  actions: JustAddedActions;
  onAcknowledge: (ids?: string[]) => void;
  onChanged?: () => void | Promise<void>;
}) {
  const popups = useEditPopups();
  const created = entry.created;
  const filed = entry.state === "filed";
  const unplaced = needsLook(entry);
  const detail = unplaced ? null : detailOf(entry);
  const where = created?.project ?? created?.area;
  const waiting = isWaiting(entry);
  // The bin picked on the pill: its form opens in the row once the entry is ready for one.
  const [openAs, setOpenAs] = useState<Kind | null>(null);

  // --- The countdown on the ✓ ---------------------------------------------------------------
  // The acknowledge call changes identity with every render of the card, so it is read through a
  // ref: an effect depending on it restarted the timer on every refresh, and undid a stop.
  const acknowledgeRef = useRef(onAcknowledge);
  acknowledgeRef.current = onAcknowledge;
  const timer = useRef<PausableTimeout | null>(null);
  const stopped = useRef(false);
  const [clearsInMs, setClearsInMs] = useState<number | null>(null);
  const [held, setHeld] = useState(false);
  const counting = filed && !entry.enriching;

  useEffect(() => {
    if (!counting || stopped.current) return;
    const delay = autoClearDelay(true, loadSettings(), prefersReducedMotion());
    if (delay === null) return;
    setClearsInMs(delay);
    timer.current = pausableTimeout(() => acknowledgeRef.current([entry.id]), delay);
    return () => {
      timer.current?.cancel();
      timer.current = null;
    };
  }, [counting, entry.id]);

  /** The person is working on this row: it stays until they let it go. */
  const stop = useCallback(() => {
    stopped.current = true;
    timer.current?.cancel();
    timer.current = null;
    setClearsInMs(null);
    setHeld(false);
  }, []);

  // "Change to" and "File as" only open the bin's form. A filed line is left exactly as it is until
  // the form is saved, or sent to the AI, which takes back what was made as part of that; Cancel
  // therefore returns to the line as it was, with everything the task or note had.
  function pick(kind: Kind) {
    stop();
    setOpenAs(kind);
  }

  // The form's calls, on this line. On a line that is already filed they replace what it made.
  const rowActions: JustAddedActions = useMemo(
    () => ({
      resolve: async (item, request, recurrence) => {
        try {
          await actions.resolve(item, request, recurrence, filed);
        } catch (err) {
          postNotice(err instanceof Error ? err.message : "Couldn't file that.");
          return;
        }
        setOpenAs(null);
      },
      retryAi: actions.retryAi,
      refineAi: (item, body, recurrence) => actions.refineAi(item, body, recurrence, filed),
      settled: async (item, result) => {
        await actions.settled(item, result);
        setOpenAs(null);
      },
    }),
    [actions, filed],
  );

  const ackLabel = filed ? `Looks right: ${created?.title ?? entry.text}` : `Send to Inbox: ${entry.text}`;

  return (
    <li
      className="add-result"
      onPointerEnter={() => {
        if (!timer.current) return;
        timer.current.pause();
        setHeld(true);
      }}
      onPointerLeave={() => {
        if (!timer.current) return;
        timer.current.resume();
        setHeld(false);
      }}
      onClick={stop}
      onFocus={stop}
    >
      <div className="add-result-meta">
        <AddResultPill entry={entry} onPick={pick} onOpen={stop} />
        {where && <span className="small muted">in {where}</span>}
        <span className="add-result-spacer" />
        {(created?.kind === "project" || created?.kind === "context") && (
          <button
            type="button"
            className="btn ghost small"
            aria-label={`Edit: ${created.title}`}
            onClick={() => openFiled(popups, created.kind, created.id)}
          >
            <Pencil />
          </button>
        )}
        {!waiting &&
          (filed ? (
            <button
              type="button"
              className="btn ghost small add-ack"
              aria-label={ackLabel}
              title={clearsInMs !== null ? "Looks right. Clears on its own; touch the row to keep it." : "Looks right"}
              onClick={() => onAcknowledge([entry.id])}
            >
              <Check size={14} />
              {clearsInMs !== null && <Ring ms={clearsInMs} paused={held} />}
            </button>
          ) : (
            <button
              type="button"
              className="btn ghost small"
              aria-label={ackLabel}
              onClick={() => onAcknowledge([entry.id])}
            >
              Send to Inbox
            </button>
          ))}
      </div>
      {wasReworded(entry) && (
        <p className="add-result-wrote small muted">You wrote: {entry.text}</p>
      )}
      {detail && <p className="add-result-detail small muted">{detail}</p>}
      {openAs && (filed || unplaced) && entry.item ? (
        <>
          {filed && created && (
            <p className="add-result-detail small muted">
              Saving, or asking the AI, replaces the {created.kind} "{created.title}".
            </p>
          )}
          <ul className="tasks">
            <InboxRow
              key={openAs}
              item={{ ...entry.item, state: "unresolved" } as InboxItem}
              areas={areas}
              projects={projects}
              onResolve={rowActions.resolve}
              onRetryAi={rowActions.retryAi}
              onRefineAi={rowActions.refineAi}
              onRetryAiSettled={rowActions.settled}
              initialMode={openAs}
              embedded
              onClose={() => setOpenAs(null)}
            />
          </ul>
        </>
      ) : filed && created ? (
        <FiledBody entry={entry} areas={areas} projects={projects} onChanged={onChanged} />
      ) : unplaced && entry.item ? (
        <ul className="tasks">
          <InboxRow
            item={{ ...entry.item, state: "unresolved" } as InboxItem}
            areas={areas}
            projects={projects}
            onResolve={rowActions.resolve}
            onRetryAi={rowActions.retryAi}
            onRefineAi={rowActions.refineAi}
            onRetryAiSettled={rowActions.settled}
            embedded
          />
        </ul>
      ) : (
        <p className="add-result-text">{entry.text}</p>
      )}
    </li>
  );
}

/** What a filed line made, as heavy as its own list shows it. */
function FiledBody({
  entry,
  areas,
  projects,
  onChanged,
}: {
  entry: AddEntry;
  areas: Area[];
  projects: Project[];
  onChanged?: () => void | Promise<void>;
}) {
  const created = entry.created!;
  if (created.kind === "task") {
    return (
      <FiledTask
        id={created.id}
        enriching={entry.enriching}
        areas={areas}
        projects={projects}
        onChanged={onChanged}
      />
    );
  }
  if (created.kind === "note") {
    return (
      <FiledNote id={created.id} areas={areas} projects={projects} onChanged={onChanged} />
    );
  }
  if (created.kind === "project") {
    return (
      <FiledProject
        id={created.id}
        name={created.title}
        areas={areas}
        projects={projects}
        onChanged={onChanged}
      />
    );
  }
  return (
    <div className="add-result-made">
      <p className="add-result-text">
        <strong>{created.title}</strong>
      </p>
    </div>
  );
}

/** A project the AI (or the Project chip) made: its name, the outline lightbulb on a line of its own
 * beneath it, and always the project's tasks as the real task list (with the box to add another), to
 * review, change or delete - the outline's tasks land in it, and so does whatever else goes there. */
function FiledProject({
  id,
  name,
  areas,
  projects,
  onChanged,
}: {
  id: string;
  name: string;
  areas: Area[];
  projects: Project[];
  onChanged?: () => void | Promise<void>;
}) {
  const burst = useFireworks();
  const actions = useMemo(() => basicTaskActions(burst), [burst]);
  // Bumped each time the outline adds or takes back tasks, so the list below re-reads.
  const [outlines, setOutlines] = useState(0);
  return (
    <div className="add-result-made">
      <p className="add-result-text">
        <strong>{name}</strong>
      </p>
      <ProjectOutlineButton
        projectId={id}
        onAdded={() => {
          setOutlines((n) => n + 1);
          void onChanged?.();
        }}
      />
      <TaskDrawer
        kind="project"
        containerId={id}
        containerName={name}
        today={todayLocalDate()}
        areas={areas}
        projects={projects}
        actions={actions}
        onChanged={onChanged}
        refreshKey={outlines}
      />
    </div>
  );
}

/** Fetch one made thing and keep it current: reloaded when the AI finishes polishing it. */
function useMade<T>(load: () => Promise<T>, enriching: boolean) {
  const [item, setItem] = useState<T | null>(null);
  const [gone, setGone] = useState(false);
  const loadRef = useRef(load);
  loadRef.current = load;
  const reload = useCallback(async () => {
    try {
      setItem(await loadRef.current());
      setGone(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setGone(true);
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload, enriching]);
  // An edit popup saved, or deleted, it: show what is true now.
  useEffect(() => {
    const onEdited = () => void reload();
    window.addEventListener(ITEM_EDITED_EVENT, onEdited);
    return () => window.removeEventListener(ITEM_EDITED_EVENT, onEdited);
  }, [reload]);
  return { item, gone, reload };
}

function FiledTask({
  id,
  enriching,
  areas,
  projects,
  onChanged,
}: {
  id: string;
  enriching: boolean;
  areas: Area[];
  projects: Project[];
  onChanged?: () => void | Promise<void>;
}) {
  const burst = useFireworks();
  // What the row's breakdown split button can offer (its AI bulb): the Tasks card computes it once
  // for the list and hands it down, and a row drawn on its own has to as well.
  const aiTiers = useAiTierAvailability();
  const { item: task, gone, reload } = useMade<Task>(() => api.task(id), enriching);
  const actions = useMemo(() => {
    const base = basicTaskActions(burst);
    // Every change reloads this row and tells the lists around the card. The wrapper is generic
    // over the action's signature, which the object spread below cannot express in types.
    const wrapped: Record<string, unknown> = {};
    for (const [name, fn] of Object.entries(base)) {
      wrapped[name] = async (...args: unknown[]) => {
        const result = await (fn as (...a: unknown[]) => Promise<unknown>)(...args);
        await reload();
        await onChanged?.();
        return result;
      };
    }
    return wrapped as TaskActions;
  }, [burst, reload, onChanged]);

  if (gone) return <p className="muted small">That task is gone.</p>;
  if (!task) return <p className="muted small">Loading…</p>;
  return (
    <ul className="tasks">
      <TaskRow
        task={task}
        today={todayLocalDate()}
        areas={areas}
        projects={projects}
        actions={actions}
        aiTiers={aiTiers}
      />
    </ul>
  );
}

function FiledNote({
  id,
  areas,
  projects,
  onChanged,
}: {
  id: string;
  areas: Area[];
  projects: Project[];
  onChanged?: () => void | Promise<void>;
}) {
  const { item: note, gone, reload } = useMade<Note>(() => api.note(id), false);
  if (gone) return <p className="muted small">That note is gone.</p>;
  if (!note) return <p className="muted small">Loading…</p>;
  const changed = async () => {
    await reload();
    await onChanged?.();
  };
  return (
    <ul className="note-list">
      <NoteRow
        note={note}
        areas={areas}
        projects={projects}
        onEdit={async (noteId, patch) => {
          await api.patchNote(noteId, patch);
          await changed();
        }}
        onEditWithAi={async (noteId, values, tier) => {
          const refined = await refineNoteInPlace(noteId, { ...values, ...(tier ? { tier } : {}) });
          await changed();
          return refined;
        }}
        onDelete={async (noteId) => {
          await api.deleteNote(noteId);
          await changed();
        }}
      />
    </ul>
  );
}
